import React from 'react';
import {
  Box,
  Chip,
  Divider,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import {
  Briefcase,
  Building2,
  Calendar,
  Clock,
} from 'lucide-react';
import { WorkExperience } from '../../../../../utils/types/JobOpenings';

export interface IExperienceTabProps {
  experience?: WorkExperience[];
}

interface GroupedExperience {
  company: string;
  roles: WorkExperience[];
  totalDuration: string;
  startDate: number;
  endDate: number | null;
}

export const ExperienceTab = ({ experience }: IExperienceTabProps) => {
  const formatDate = (timestamp: number): string | null => {
    if (!timestamp) return null;
    const date = new Date(timestamp * 1000);
    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
    });
  };

  const getDifferenceInYears = (start?: number, end?: number): string => {
    if (!start) return '';

    const startDate = new Date(start * 1000);
    const endDate = end ? new Date(end * 1000) : new Date();

    const diffTime = endDate.getTime() - startDate.getTime();
    const diffYears = Math.floor(diffTime / (1000 * 60 * 60 * 24 * 365.25));
    const diffMonths = Math.floor(
      (diffTime % (1000 * 60 * 60 * 24 * 365.25)) / (1000 * 60 * 60 * 24 * 30.44)
    );

    if (diffYears === 0 && diffMonths === 0) return 'Less than a month';
    if (diffYears === 0) return `${diffMonths} ${diffMonths === 1 ? 'mo' : 'mos'}`;
    if (diffMonths === 0) return `${diffYears} ${diffYears === 1 ? 'yr' : 'yrs'}`;
    return `${diffYears} ${diffYears === 1 ? 'yr' : 'yrs'} ${diffMonths} ${diffMonths === 1 ? 'mo' : 'mos'}`;
  };

  const calculateTotalExperience = (): string => {
    if (!experience || experience.length === 0) return '';

    let totalMonths = 0;
    experience.forEach((exp) => {
      if (exp.startDate) {
        const start = new Date(exp.startDate * 1000);
        const end = exp.endDate ? new Date(exp.endDate * 1000) : new Date();
        const diff = end.getTime() - start.getTime();
        totalMonths += Math.floor(diff / (1000 * 60 * 60 * 24 * 30.44));
      }
    });

    const years = Math.floor(totalMonths / 12);
    const months = totalMonths % 12;

    if (years === 0) return `${months} ${months === 1 ? 'mo' : 'mos'}`;
    if (months === 0) return `${years} ${years === 1 ? 'yr' : 'yrs'}`;
    return `${years} ${years === 1 ? 'yr' : 'yrs'} ${months} ${months === 1 ? 'mo' : 'mos'}`;
  };

  const groupExperiencesByCompany = (): GroupedExperience[] => {
    if (!experience || experience.length === 0) return [];

    const grouped = experience.reduce((acc, exp) => {
      const company = exp.company || 'Unknown Company';

      if (!acc[company]) {
        acc[company] = [];
      }
      acc[company].push(exp);
      return acc;
    }, {} as Record<string, WorkExperience[]>);

    return Object.entries(grouped)
      .map(([company, roles]) => {
        const sortedRoles = roles.sort(
          (a, b) => (b.startDate || 0) - (a.startDate || 0)
        );

        const startDate = Math.min(...sortedRoles.map((r) => r.startDate || 0));
        const endDate = Math.max(
          ...sortedRoles.map((r) => r.endDate || Date.now() / 1000)
        );
        const hasCurrentRole = sortedRoles.some((r) => !r.endDate);

        return {
          company,
          roles: sortedRoles,
          totalDuration: getDifferenceInYears(
            startDate,
            hasCurrentRole ? undefined : endDate
          ),
          startDate,
          endDate: hasCurrentRole ? null : endDate,
        };
      })
      .sort((a, b) => (b.startDate || 0) - (a.startDate || 0));
  };

  const groupedExperiences = groupExperiencesByCompany();

  if (groupedExperiences.length === 0) {
    return (
      <Paper
        elevation={0}
        sx={{
          py: 8,
          px: 3,
          textAlign: 'center',
          borderRadius: '16px',
          border: '2px dashed',
          borderColor: 'divider',
          bgcolor: 'grey.50',
          my: 2,
        }}
      >
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: '50%',
            bgcolor: 'rgba(37, 99, 235, 0.08)',
            color: '#2563eb',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mx: 'auto',
            mb: 2,
          }}
        >
          <Briefcase size={28} />
        </Box>
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}
        >
          No Work Experience
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: 'text.secondary', maxWidth: 300, mx: 'auto' }}
        >
          No employment history has been recorded for this candidate.
        </Typography>
      </Paper>
    );
  }

  return (
    <Box sx={{ py: 1.5, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      {/* Header */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ pb: 1, borderBottom: '1px solid', borderColor: 'divider' }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Box
            sx={{
              p: 0.75,
              borderRadius: '8px',
              bgcolor: 'rgba(37, 99, 235, 0.1)',
              color: '#2563eb',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Briefcase size={18} />
          </Box>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 700, fontSize: '15px', color: 'text.primary' }}
          >
            Work Experience
          </Typography>
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center">
          {experience && experience.length > 0 && (
            <Chip
              icon={<Clock size={13} />}
              label={`Total: ${calculateTotalExperience()}`}
              size="small"
              sx={{
                fontWeight: 600,
                fontSize: '11.5px',
                bgcolor: 'rgba(37, 99, 235, 0.08)',
                color: '#1d4ed8',
                border: '1px solid rgba(37, 99, 235, 0.2)',
              }}
            />
          )}
        </Stack>
      </Stack>

      {/* Companies Timeline List */}
      <Stack spacing={2.5}>
        {groupedExperiences.map((group, groupIdx) => (
          <Paper
            key={groupIdx}
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: '12px',
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
              transition: 'all 0.2s ease',
              '&:hover': {
                borderColor: 'grey.400',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            {/* Company Row */}
            <Stack
              direction="row"
              alignItems="flex-start"
              justifyContent="space-between"
              sx={{ mb: 1.5 }}
            >
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: '8px',
                    bgcolor: 'grey.100',
                    color: 'text.secondary',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Building2 size={18} />
                </Box>
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 700, fontSize: '14px', color: 'text.primary' }}
                  >
                    {group.company}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: 'text.secondary', fontSize: '12px' }}
                  >
                    {formatDate(Number(group.startDate))} –{' '}
                    {group.endDate ? formatDate(Number(group.endDate)) : 'Present'}
                  </Typography>
                </Box>
              </Stack>

              <Chip
                label={group.totalDuration}
                size="small"
                variant="outlined"
                sx={{
                  height: '24px',
                  fontSize: '11px',
                  fontWeight: 600,
                  borderColor: 'grey.300',
                  color: 'text.secondary',
                }}
              />
            </Stack>

            <Divider sx={{ my: 1.5 }} />

            {/* Roles inside Company */}
            <Stack spacing={1.5}>
              {group.roles.map((role, roleIdx) => (
                <Box
                  key={roleIdx}
                  sx={{
                    p: 1.5,
                    borderRadius: '8px',
                    bgcolor: 'grey.50',
                    border: '1px solid',
                    borderColor: 'grey.200',
                  }}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ mb: 0.5 }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, color: 'text.primary' }}
                    >
                      {role.jobTitle}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: 'text.secondary', fontWeight: 500 }}
                    >
                      {getDifferenceInYears(role.startDate, role.endDate)}
                    </Typography>
                  </Stack>

                  <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.75 }}>
                    <Calendar size={13} color="#64748b" />
                    <Typography
                      variant="caption"
                      sx={{ color: 'text.secondary', fontSize: '11.5px' }}
                    >
                      {formatDate(Number(role.startDate))} –{' '}
                      {role.endDate ? formatDate(Number(role.endDate)) : 'Present'}
                    </Typography>
                  </Stack>

                  {role.description && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'text.secondary',
                        lineHeight: 1.6,
                        display: 'block',
                        mt: 1,
                        bgcolor: 'background.paper',
                        p: 1.25,
                        borderRadius: '6px',
                        border: '1px solid',
                        borderColor: 'grey.200',
                      }}
                    >
                      {role.description}
                    </Typography>
                  )}
                </Box>
              ))}
            </Stack>
          </Paper>
        ))}
      </Stack>
    </Box>
  );
};
